import React, { useState, useEffect } from "react";
import axiosInstance from "../../lib/auth/axiosApi";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

// Read-only market/econ data from the OpenBB sidecar (services/openbb).
// The backend injects the user's own keys; here we only pick symbol + provider.
const PROVIDERS = ["cboe", "yfinance", "fmp", "polygon", "tiingo", "intrinio"];

const label = { color: "#bbb", fontSize: 12, marginBottom: 2 };
const input = {
  padding: "6px 8px",
  borderRadius: 4,
  border: "1px solid #444",
  background: "#1e1e1e",
  color: "#eee",
};
const btn = {
  padding: "6px 12px",
  borderRadius: 4,
  border: "none",
  background: "#ffd23f",
  color: "#1a1a1a",
  fontWeight: 600,
  cursor: "pointer",
};

function OpenBBPage() {
  const [symbol, setSymbol] = useState("SPY");
  const [provider, setProvider] = useState("cboe");
  const [quote, setQuote] = useState(null);
  const [history, setHistory] = useState([]);
  const [results, setResults] = useState([]);
  const [query, setQuery] = useState("");
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axiosInstance
      .get("openbb/health/")
      .then((r) => setHealth(!!r.data.ok))
      .catch(() => setHealth(false));
  }, []);

  const call = async (fn) => {
    setLoading(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "request failed");
    } finally {
      setLoading(false);
    }
  };

  const getQuote = () =>
    call(async () => {
      const r = await axiosInstance.get("openbb/quote/", {
        params: { symbol, provider },
      });
      const rows = r.data.results;
      setQuote(Array.isArray(rows) ? rows[0] : rows);
    });

  const getHistory = () =>
    call(async () => {
      const r = await axiosInstance.get("openbb/historical/", {
        params: { symbol, provider, interval: "1d" },
      });
      const rows = r.data.results || [];
      setHistory(
        rows
          .filter((b) => b && b.close != null)
          .map((b) => ({ date: String(b.date || "").slice(0, 10), close: Number(b.close) }))
      );
    });

  const doSearch = () =>
    call(async () => {
      const r = await axiosInstance.get("openbb/search/", {
        params: { query, provider },
      });
      setResults((r.data.results || []).slice(0, 50));
    });

  return (
    <div style={{ padding: 16, color: "#eee", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>OpenBB — market data</h2>
        <span
          style={{
            fontSize: 11,
            padding: "2px 8px",
            borderRadius: 10,
            background: health == null ? "#555" : health ? "#1f6f3f" : "#7a2b2b",
          }}
        >
          {health == null ? "checking…" : health ? "service up" : "service down"}
        </span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", marginBottom: 14 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={label}>Symbol</span>
          <input
            style={{ ...input, width: 110, textTransform: "uppercase" }}
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={label}>Provider</span>
          <select style={{ ...input, width: 130 }} value={provider} onChange={(e) => setProvider(e.target.value)}>
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <button style={btn} disabled={loading} onClick={getQuote}>
          Quote
        </button>
        <button style={btn} disabled={loading} onClick={getHistory}>
          History
        </button>
      </div>

      {error && (
        <div style={{ background: "#3a1f1f", color: "#ffb4b4", padding: "8px 10px", borderRadius: 4, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {quote && (
        <div style={{ marginBottom: 18 }}>
          <h4 style={{ margin: "0 0 6px" }}>Quote — {symbol}</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 6 }}>
            {Object.entries(quote)
              .filter(([, v]) => v != null && typeof v !== "object")
              .slice(0, 24)
              .map(([k, v]) => (
                <div key={k} style={{ background: "#1e1e1e", borderRadius: 4, padding: "6px 8px" }}>
                  <div style={{ fontSize: 10, color: "#999" }}>{k}</div>
                  <div style={{ fontSize: 13 }}>{String(v)}</div>
                </div>
              ))}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <h4 style={{ margin: "0 0 6px" }}>
            {symbol} — close ({history.length} bars)
          </h4>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={history} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: "#999", fontSize: 10 }} minTickGap={40} />
                <YAxis tick={{ fill: "#999", fontSize: 10 }} domain={["auto", "auto"]} width={55} />
                <Tooltip contentStyle={{ background: "#1e1e1e", border: "1px solid #444", color: "#eee" }} />
                <Line type="monotone" dataKey="close" stroke="#ffd23f" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div style={{ borderTop: "1px solid #333", paddingTop: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <span style={label}>Search symbols</span>
            <input
              style={input}
              value={query}
              placeholder="e.g. apple, SPY, treasury"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
            />
          </div>
          <button style={btn} disabled={loading} onClick={doSearch}>
            Search
          </button>
        </div>
        {results.length > 0 && (
          <div style={{ maxHeight: 180, overflow: "auto" }}>
            {results.map((row, i) => (
              <div
                key={i}
                style={{ padding: "4px 6px", borderBottom: "1px solid #262626", cursor: "pointer", fontSize: 13 }}
                onClick={() => setSymbol(String(row.symbol || row.ticker || "").toUpperCase())}
              >
                <strong>{row.symbol || row.ticker}</strong> {row.name || row.description || ""}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OpenBBPage;
